/** V2 — Primitives UI mobile-first du tunnel. Couleurs = tokens (styles/tokens.css) ; en DA v2 les emojis deviennent des icônes Lucide. */
import { useRef, useState } from "react";
import { Home, Umbrella, Coins, Building2, Hammer, Trees, HelpCircle, Key, Building, MapPin, Wrench, Users, User, Briefcase, Banknote, Wallet, CreditCard, Baby, Target, Zap, Phone, FileText, Rocket, Check, X, Sparkles, PartyPopper, CircleDot, Landmark, Bot } from "lucide-react";
import { isDAv2 } from "@/lib/da";

export const GRAD = "var(--cf-gradient)";

const ICONS = {
  "🏡": Home, "🏠": Home, "🏖️": Umbrella, "💰": Coins, "🏗️": Building2, "🌳": Trees, "🤷": HelpCircle, "❔": HelpCircle, "🔑": Key, "🏢": Building,
  "📍": MapPin, "🔨": Wrench, "👥": Users, "👤": User, "👨‍👧": Baby, "💼": Briefcase, "💶": Banknote, "💵": Wallet, "💳": CreditCard, "🏦": Landmark,
  "🎯": Target, "⚡": Zap, "📞": Phone, "📝": FileText, "🚀": Rocket, "✅": Check, "❌": X, "✨": Sparkles, "🎉": PartyPopper, "🟢": CircleDot, "🟠": CircleDot, "🔴": CircleDot, "🧑": User, "🧑‍🤝‍🧑": Users, "🤖": Bot,
};

/** Emoji en DA par défaut, icône Lucide sobre en DA v2 (même call-site). */
export function SimIcon({ emoji, size = "md", color, className = "" }) {
  if (!emoji) return null;
  const Icon = ICONS[emoji];
  if (!isDAv2() || !Icon) return <span className={`leading-none ${size === "lg" ? "text-4xl" : size === "xl" ? "text-5xl" : size === "sm" ? "text-lg" : "text-2xl"} ${className}`}>{emoji}</span>;
  const px = size === "xl" ? 28 : size === "lg" ? 24 : size === "sm" ? 16 : 20;
  const box = size === "xl" ? "w-14 h-14" : size === "lg" ? "w-12 h-12" : size === "sm" ? "w-7 h-7" : "w-10 h-10";
  return (
    <span className={`inline-flex items-center justify-center rounded-full shrink-0 ${box} ${className}`}
      style={{ background: color ? `color-mix(in srgb, ${color} 12%, white)` : "var(--cf-primary-soft)", color: color || "var(--cf-primary)" }} aria-hidden="true">
      <Icon size={px} strokeWidth={2} />
    </span>
  );
}

export function BigButton({ children, onClick, disabled, testid, tone = "primary" }) {
  const bg = disabled ? "var(--cf-border-strong)" : tone === "dark" ? "var(--cf-text)" : GRAD;
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`w-full min-h-[52px] rounded-2xl text-white font-extrabold text-[15px] active:scale-[0.98] transition sim-btn-primary ${tone === "dark" ? "sim-btn-dark" : ""} ${disabled ? "cursor-not-allowed" : "shadow-lg shadow-blue-500/20"}`}
      style={{ background: bg }}>
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="w-full min-h-[44px] rounded-2xl text-slate-500 font-bold text-[13px] hover:text-slate-800 hover:bg-slate-100 transition">
      {children}
    </button>
  );
}

export function ChoiceCard({ selected, emoji, label, hint, onClick, testid, compact }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={!!selected}
      className={`w-full text-left rounded-2xl border-2 transition-all flex items-center gap-3 sim-choice ${compact ? "p-3 min-h-[52px]" : "p-4 min-h-[64px]"} ${
        selected ? "border-[color:var(--cf-primary)] bg-[color:var(--cf-primary-soft)] shadow-md" : "border-slate-200 bg-white hover:border-slate-300"}`}>
      {emoji && <SimIcon emoji={emoji} />}
      <div className="flex-1 min-w-0">
        <div className={`font-extrabold text-slate-900 leading-tight ${compact ? "text-[14px]" : "text-[15px]"}`}>{label}</div>
        {hint && <div className="text-[12px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
      {selected && <div className="w-6 h-6 rounded-full bg-[color:var(--cf-primary)] text-white flex items-center justify-center shrink-0"><Check size={14} strokeWidth={3} /></div>}
    </button>
  );
}

export function Chips({ options, value, onChange, testid, cols = 3 }) {
  return (
    <div className={`grid gap-2 ${cols === 2 ? "grid-cols-2" : cols === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)} data-testid={`${testid}-${o.key}`} aria-pressed={value === o.key}
          className={`min-h-[46px] px-2 rounded-xl border-2 font-bold text-[13px] leading-tight transition sim-chip ${
            value === o.key ? "border-[color:var(--cf-primary)] bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary-text)]" : "border-slate-200 bg-white text-slate-700"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function YesNo({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <ChoiceCard compact selected={value === true} emoji="✅" label="Oui" onClick={() => onChange(true)} testid={`${testid}-oui`} />
      <ChoiceCard compact selected={value === false} emoji="❌" label="Non" onClick={() => onChange(false)} testid={`${testid}-non`} />
    </div>
  );
}

export function StepTitle({ emoji, title, hint }) {
  return (
    <div className="mb-5">
      {emoji && <div className="mb-3"><SimIcon emoji={emoji} /></div>}
      <h1 className="font-extrabold text-slate-900 text-[22px] md:text-[26px] leading-tight tracking-tight">{title}</h1>
      {hint && <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

export function SubLabel({ children }) {
  return <div className="text-[13px] font-bold text-slate-700 mb-2 mt-5">{children}</div>;
}

export function AmountInput({ value, onChange, suffix = "€", placeholder = "0", testid, autoFocus, small }) {
  return (
    <div className={`rounded-2xl bg-white border-2 border-slate-200 focus-within:border-[color:var(--cf-primary)] flex items-center gap-2 sim-field ${small ? "p-3" : "p-4"}`}>
      <input type="text" inputMode="numeric" pattern="[0-9]*" value={value ?? ""} autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))} placeholder={placeholder}
        className={`flex-1 min-w-0 font-extrabold text-slate-900 outline-none bg-transparent tabular-nums placeholder:text-slate-300 ${small ? "text-xl" : "text-3xl"}`}
        data-testid={testid} />
      <span className={`font-bold text-slate-400 ${small ? "text-sm" : "text-xl"}`}>{suffix}</span>
    </div>
  );
}

export function TextField({ label, value, onChange, testid, type = "text", inputMode, autoComplete, placeholder }) {
  return (
    <div className="rounded-xl bg-white border-2 border-slate-200 focus-within:border-[color:var(--cf-primary)] p-3 sim-field">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <input type={type} inputMode={inputMode} autoComplete={autoComplete} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-[16px] font-bold text-slate-900 outline-none bg-transparent placeholder:text-slate-300 mt-0.5" data-testid={testid} />
    </div>
  );
}

/** Ville ou code postal — autocomplétion API Adresse (gouv), fallback saisie libre. */
export function CityInput({ ville, codePostal, onChange, testid, placeholder = "Ville ou code postal" }) {
  const [q, setQ] = useState(ville ? `${ville}${codePostal ? ` (${codePostal})` : ""}` : "");
  const [sugg, setSugg] = useState([]);
  const timer = useRef(null);
  const search = (text) => {
    setQ(text);
    onChange({ ville: text.trim(), code_postal: /^\d{5}$/.test(text.trim()) ? text.trim() : "" });
    clearTimeout(timer.current);
    if (text.trim().length < 2) { setSugg([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(text)}&type=municipality&limit=5`);
        const j = await r.json();
        setSugg((j.features || []).map((f) => ({ ville: f.properties.city || f.properties.name, cp: f.properties.postcode })));
      } catch { setSugg([]); }
    }, 250);
  };
  const pick = (s) => { setQ(`${s.ville} (${s.cp})`); setSugg([]); onChange({ ville: s.ville, code_postal: s.cp }); };
  return (
    <div className="relative">
      <div className="rounded-2xl bg-white border-2 border-slate-200 focus-within:border-[color:var(--cf-primary)] p-3 sim-field">
        <input type="text" value={q} onChange={(e) => search(e.target.value)} placeholder={placeholder} autoComplete="off"
          className="w-full text-[17px] font-bold text-slate-900 outline-none bg-transparent placeholder:text-slate-300" data-testid={testid} />
      </div>
      {sugg.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden" data-testid={`${testid}-suggestions`}>
          {sugg.map((s, i) => (
            <button key={`${s.cp}-${i}`} type="button" onClick={() => pick(s)} className="w-full text-left px-4 py-3 text-[14px] font-bold text-slate-800 hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`${testid}-sugg-${i}`}>
              {s.ville} <span className="text-slate-400 font-semibold">{s.cp}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
